import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  MinusCircle,
  HelpCircle,
  Newspaper,
  Activity,
  Building2,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import TopBar from "../components/TopBar";
import TabNav from "../components/TabNav";
import { apiClient } from "../lib/api";
import { fmtNum } from "../lib/format";

function fmtCr(val) {
  if (val == null) return "—";
  const cr = val / 1e7;
  if (cr >= 1e5) return `₹${(cr / 1e5).toFixed(2)} L Cr`;
  if (cr >= 1000) return `₹${(cr / 1000).toFixed(2)}K Cr`;
  return `₹${cr.toFixed(0)} Cr`;
}

function fmtRatio(v, suffix = "") {
  if (v == null || isNaN(v)) return "—";
  return `${Number(v).toFixed(2)}${suffix}`;
}

function fmtPctRatio(v) {
  if (v == null || isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
}

const verdictStyle = {
  STRONG_BUY: "bg-profit text-white border-transparent lime-glow",
  BUY: "bg-[hsl(var(--profit)/0.12)] text-profit border-[hsl(var(--profit)/0.4)]",
  HOLD: "bg-saffron-soft text-saffron border-[hsl(var(--saffron)/0.4)]",
  REDUCE: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)]",
  AVOID: "bg-[hsl(var(--loss)/0.12)] text-loss border-[hsl(var(--loss)/0.4)]",
};

const checklistIcon = {
  PASS: <CheckCircle2 className="h-4 w-4 text-profit" strokeWidth={2} />,
  FAIL: <XCircle className="h-4 w-4 text-loss" strokeWidth={2} />,
  NEUTRAL: <MinusCircle className="h-4 w-4 text-[hsl(var(--warning))]" strokeWidth={2} />,
  UNKNOWN: <HelpCircle className="h-4 w-4 text-muted-foreground" strokeWidth={2} />,
};

function MetricCell({ label, value }) {
  return (
    <div className="card-noir p-3 rounded-md">
      <div className="text-[9px] text-muted-foreground uppercase tracking-[0.24em] font-mono">{label}</div>
      <div className="font-mono text-base font-semibold tabular mt-1 text-foreground">{value}</div>
    </div>
  );
}

function QuarterlyTable({ rows, title, fields }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="card-noir p-6 text-center text-xs text-muted-foreground rounded-lg">
        {title}: data unavailable.
      </div>
    );
  }
  return (
    <div className="card-noir rounded-lg overflow-x-auto">
      <div className="p-3 border-b border-border text-[10px] uppercase tracking-[0.24em] font-mono font-bold text-lime">
        {title}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono border-b border-border">
            <th className="text-left p-3 font-semibold">Line</th>
            {rows.map((r) => (
              <th key={r.period} className="text-right p-3 font-mono font-semibold">
                {r.period}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f} className="border-b border-border last:border-b-0">
              <td className="p-3 text-muted-foreground">{f}</td>
              {rows.map((r) => (
                <td key={r.period + f} className="p-3 text-right font-mono tabular">
                  {r[f] != null ? fmtCr(r[f]) : "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StockDetail() {
  const { symbol: raw } = useParams();
  const nav = useNavigate();
  const symbol = decodeURIComponent(raw || "");
  const [tab, setTab] = useState("checklist");
  const [fundamentals, setFundamentals] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [news, setNews] = useState([]);
  const [loadingFund, setLoadingFund] = useState(true);
  const [loadingAnalysis, setLoadingAnalysis] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadFundamentals = useCallback(async () => {
    setLoadingFund(true);
    try {
      const f = await apiClient.get(`/stocks/${encodeURIComponent(symbol)}/fundamentals`);
      setFundamentals(f.data);
      const n = await apiClient.get(`/stocks/${encodeURIComponent(symbol)}/news`);
      setNews(n.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load fundamentals");
    } finally {
      setLoadingFund(false);
    }
  }, [symbol]);

  const loadAnalysis = useCallback(async (force = false) => {
    setLoadingAnalysis(true);
    try {
      const url = `/stocks/${encodeURIComponent(symbol)}/analysis${force ? "?force=true" : ""}`;
      const a = await apiClient.get(url, { timeout: 120000 });
      setAnalysis(a.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI analysis unavailable");
    } finally {
      setLoadingAnalysis(false);
    }
  }, [symbol]);

  useEffect(() => {
    loadFundamentals();
    loadAnalysis(false);
  }, [loadFundamentals, loadAnalysis]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await apiClient.post(`/stocks/${encodeURIComponent(symbol)}/refresh`, null, { timeout: 120000 });
      toast.success("Analysis refreshed");
      await loadFundamentals();
      await loadAnalysis(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const a = analysis?.analysis;
  const ratios = fundamentals?.ratios || {};

  const tabs = [
    { id: "checklist", label: "Checklist", icon: <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "fundamentals", label: "Fundamentals", icon: <Activity className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "financials", label: "Financials", icon: <FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "news", label: "News & Catalysts", icon: <Newspaper className="h-3.5 w-3.5" strokeWidth={1.5} />, count: news.length },
    { id: "verdict", label: "Verdict", icon: <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.5} /> },
  ];

  return (
    <div className="min-h-screen bg-background">
      <TopBar subtitle="Stock Intelligence" />
      <main className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 max-w-[1600px] mx-auto">
        <button
          onClick={() => nav(-1)}
          data-testid="stock-back"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-lime mb-5 font-mono uppercase tracking-[0.2em] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          Back
        </button>

        {/* Hero header */}
        <div className="card-noir p-6 sm:p-7 rounded-lg relative overflow-hidden mb-6">
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full pointer-events-none"
               style={{ background: "radial-gradient(circle, hsl(var(--lime) / 0.12) 0%, transparent 65%)" }} />
          <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div className="min-w-0">
              <div className="text-[10px] text-muted-foreground tracking-[0.28em] uppercase font-mono flex items-center gap-1.5">
                <Building2 className="h-3 w-3" strokeWidth={1.5} />
                {fundamentals?.sector || "—"} · {fundamentals?.industry || "—"}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl font-extrabold mt-2 tracking-tighter font-mono">
                {fundamentals?.symbol || symbol}
              </h1>
              <div className="text-sm text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-1 items-center">
                {fundamentals?.long_name ? <span>{fundamentals.long_name}</span> : null}
                {fundamentals?.market_cap_category ? (
                  <span className="px-2 py-0.5 bg-secondary border border-border text-lime text-[10px] uppercase tracking-[0.2em] font-mono font-semibold rounded">
                    {fundamentals.market_cap_category}
                  </span>
                ) : null}
                {fundamentals?.market_cap ? <span className="font-mono">{fmtCr(fundamentals.market_cap)}</span> : null}
              </div>
            </div>
            <div className="flex items-center gap-3 self-start">
              {a ? (
                <span
                  data-testid="verdict-pill"
                  className={`px-4 py-2 border text-xs font-bold uppercase tracking-[0.2em] rounded-md font-mono ${
                    verdictStyle[a.verdict] || "bg-card border-border"
                  }`}
                >
                  {a.verdict?.replace("_", " ")} · {a.conviction}
                </span>
              ) : null}
              <button
                data-testid="stock-refresh"
                onClick={handleRefresh}
                disabled={refreshing}
                className="border border-border hover:border-lime hover:text-lime px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] rounded-md flex items-center gap-1.5 disabled:opacity-50 font-mono"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
                <span className="hidden sm:inline">Refresh AI</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick metrics strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <MetricCell label="LTP (₹)" value={fundamentals?.current_price != null ? fmtNum(fundamentals.current_price) : "—"} />
          <MetricCell label="52W High" value={fundamentals?.fifty_two_week_high != null ? fmtNum(fundamentals.fifty_two_week_high) : "—"} />
          <MetricCell label="52W Low" value={fundamentals?.fifty_two_week_low != null ? fmtNum(fundamentals.fifty_two_week_low) : "—"} />
          <MetricCell label="P/E (TTM)" value={fmtRatio(ratios.trailing_pe)} />
          <MetricCell label="P/B" value={fmtRatio(ratios.price_to_book)} />
          <MetricCell label="ROE" value={fmtPctRatio(ratios.return_on_equity)} />
        </div>

        <TabNav tabs={tabs} active={tab} onChange={setTab} />

        <div className="mt-6">
          {/* CHECKLIST */}
          {tab === "checklist" && (
            <section data-testid="tab-content-checklist" className="space-y-5">
              {loadingAnalysis ? (
                <div className="card-noir p-12 text-center text-sm text-muted-foreground rounded-lg font-mono">
                  Running fund-manager checklist…
                </div>
              ) : !a ? (
                <div className="card-noir p-8 text-sm text-muted-foreground rounded-lg">No analysis yet — try Refresh AI.</div>
              ) : (
                <>
                  <div className="card-noir p-6 rounded-lg">
                    <div className="text-[10px] text-lime tracking-[0.3em] uppercase font-mono font-semibold mb-3">
                      Industry tailwinds & moat
                    </div>
                    <p className="text-sm leading-relaxed mb-4 text-foreground">{a.industry_tailwinds}</p>
                    <p className="text-sm leading-relaxed text-muted-foreground">{a.competitive_position}</p>
                  </div>

                  <div className="card-noir rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-border text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-lime">
                      Fund-manager checklist
                    </div>
                    {(a.checklist || []).map((c, i) => (
                      <div key={i} className="p-4 flex items-start gap-3 border-b border-border last:border-b-0" data-testid={`checklist-${i}`}>
                        <div className="mt-0.5">{checklistIcon[c.status] || checklistIcon.UNKNOWN}</div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold">{c.item}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{c.note}</div>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground shrink-0 font-mono font-bold">
                          {c.status}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="card-noir p-5 rounded-lg border-[hsl(var(--profit)/0.25)]">
                      <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-profit mb-3 flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                        Green flags
                      </div>
                      <ul className="space-y-2 text-sm">
                        {(a.green_flags || []).map((g, i) => (
                          <li key={i} className="flex gap-2"><span className="text-profit">▸</span><span>{g}</span></li>
                        ))}
                        {(a.green_flags || []).length === 0 ? <li className="text-xs text-muted-foreground">None notable.</li> : null}
                      </ul>
                    </div>
                    <div className="card-noir p-5 rounded-lg border-[hsl(var(--loss)/0.25)]">
                      <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-loss mb-3 flex items-center gap-1.5">
                        <AlertTriangle className="h-4 w-4" strokeWidth={2} />
                        Red flags
                      </div>
                      <ul className="space-y-2 text-sm">
                        {(a.red_flags || []).map((g, i) => (
                          <li key={i} className="flex gap-2"><span className="text-loss">▸</span><span>{g}</span></li>
                        ))}
                        {(a.red_flags || []).length === 0 ? <li className="text-xs text-muted-foreground">None notable.</li> : null}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {/* FUNDAMENTALS */}
          {tab === "fundamentals" && (
            <section data-testid="tab-content-fundamentals" className="space-y-5">
              {loadingFund ? (
                <div className="card-noir p-12 text-center text-sm text-muted-foreground rounded-lg">Loading fundamentals…</div>
              ) : (
                <>
                  {fundamentals?.business_summary ? (
                    <div className="card-noir p-6 rounded-lg">
                      <div className="text-[10px] text-muted-foreground tracking-[0.3em] uppercase font-mono mb-3 font-semibold">About</div>
                      <p className="text-sm leading-relaxed">{fundamentals.business_summary}</p>
                      {fundamentals.website ? (
                        <a href={fundamentals.website} target="_blank" rel="noreferrer"
                           className="mt-3 inline-flex items-center gap-1 text-xs text-lime hover:underline font-mono">
                          {fundamentals.website} <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                        </a>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    <MetricCell label="P/E (Fwd)" value={fmtRatio(ratios.forward_pe)} />
                    <MetricCell label="PEG" value={fmtRatio(ratios.peg_ratio)} />
                    <MetricCell label="P/S" value={fmtRatio(ratios.price_to_sales)} />
                    <MetricCell label="EPS (TTM)" value={fmtRatio(ratios.trailing_eps)} />
                    <MetricCell label="ROA" value={fmtPctRatio(ratios.return_on_assets)} />
                    <MetricCell label="Debt/Equity" value={fmtRatio(ratios.debt_to_equity)} />
                    <MetricCell label="Current Ratio" value={fmtRatio(ratios.current_ratio)} />
                    <MetricCell label="Quick Ratio" value={fmtRatio(ratios.quick_ratio)} />
                    <MetricCell label="Gross Margin" value={fmtPctRatio(ratios.gross_margins)} />
                    <MetricCell label="Op. Margin" value={fmtPctRatio(ratios.operating_margins)} />
                    <MetricCell label="Net Margin" value={fmtPctRatio(ratios.profit_margins)} />
                    <MetricCell label="Rev Growth" value={fmtPctRatio(ratios.revenue_growth)} />
                    <MetricCell label="EPS Growth" value={fmtPctRatio(ratios.earnings_growth)} />
                    <MetricCell label="Div Yield" value={fmtPctRatio(ratios.dividend_yield)} />
                    <MetricCell label="Payout" value={fmtPctRatio(ratios.payout_ratio)} />
                    <MetricCell label="Beta" value={fmtRatio(ratios.beta)} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="card-noir p-5 rounded-lg">
                      <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-muted-foreground mb-3 font-semibold">Ownership</div>
                      <div className="text-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-muted-foreground">Insiders</span><span className="font-mono text-lime">{fmtPctRatio(fundamentals?.held_percent_insiders)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Institutions</span><span className="font-mono">{fmtPctRatio(fundamentals?.held_percent_institutions)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Shares O/S</span><span className="font-mono">{fundamentals?.shares_outstanding != null ? fmtNum(fundamentals.shares_outstanding/1e7) + " Cr" : "—"}</span></div>
                      </div>
                    </div>
                    <div className="card-noir p-5 rounded-lg">
                      <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-muted-foreground mb-3 font-semibold">Cap & price band</div>
                      <div className="text-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-muted-foreground">Market cap</span><span className="font-mono text-lime">{fmtCr(fundamentals?.market_cap)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">52W high</span><span className="font-mono">₹{fmtNum(fundamentals?.fifty_two_week_high)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">52W low</span><span className="font-mono">₹{fmtNum(fundamentals?.fifty_two_week_low)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Book value</span><span className="font-mono">₹{fmtNum(ratios.book_value)}</span></div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {/* FINANCIALS */}
          {tab === "financials" && (
            <section data-testid="tab-content-financials" className="space-y-5">
              <QuarterlyTable
                title="Income statement (quarterly)"
                rows={fundamentals?.quarterly_income || []}
                fields={["Total Revenue", "Operating Income", "Net Income", "EBITDA", "Basic EPS"]}
              />
              <QuarterlyTable
                title="Balance sheet (quarterly)"
                rows={fundamentals?.quarterly_balance || []}
                fields={["Total Assets", "Total Debt", "Stockholders Equity"]}
              />
              <QuarterlyTable
                title="Cash flow (quarterly)"
                rows={fundamentals?.quarterly_cashflow || []}
                fields={["Operating Cash Flow", "Free Cash Flow", "Capital Expenditure"]}
              />
              {a?.quarterly_trend ? (
                <div className="card-noir p-5 rounded-lg">
                  <div className="text-[10px] text-lime uppercase tracking-[0.3em] font-mono mb-3 font-semibold">AI take on QoQ trend</div>
                  <p className="text-sm leading-relaxed">{a.quarterly_trend}</p>
                </div>
              ) : null}
            </section>
          )}

          {/* NEWS */}
          {tab === "news" && (
            <section data-testid="tab-content-news" className="space-y-4">
              {a?.news_summary ? (
                <div className="card-noir p-5 rounded-lg">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-lime mb-3 font-semibold">AI news distillation</div>
                  <p className="text-sm leading-relaxed">{a.news_summary}</p>
                </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card-noir p-5 rounded-lg border-[hsl(var(--profit)/0.25)]">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-profit mb-3">
                    Near-term catalysts
                  </div>
                  <ul className="space-y-2 text-sm">
                    {(a?.catalysts || []).map((g, i) => (
                      <li key={i} className="flex gap-2"><span className="text-profit">▸</span><span>{g}</span></li>
                    ))}
                    {(a?.catalysts || []).length === 0 ? <li className="text-xs text-muted-foreground">No fresh catalysts.</li> : null}
                  </ul>
                </div>
                <div className="card-noir p-5 rounded-lg border-[hsl(var(--loss)/0.25)]">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-loss mb-3">
                    Key risks
                  </div>
                  <ul className="space-y-2 text-sm">
                    {(a?.risks || []).map((g, i) => (
                      <li key={i} className="flex gap-2"><span className="text-loss">▸</span><span>{g}</span></li>
                    ))}
                    {(a?.risks || []).length === 0 ? <li className="text-xs text-muted-foreground">No major risks flagged.</li> : null}
                  </ul>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-muted-foreground">
                  Latest headlines ({news.length})
                </div>
                {news.length === 0 ? (
                  <div className="card-noir p-6 text-center text-xs text-muted-foreground rounded-lg">No recent news found.</div>
                ) : (
                  <div className="card-noir rounded-lg overflow-hidden">
                    {news.map((n, i) => (
                      <a
                        key={i}
                        href={n.link || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="block p-4 border-b border-border last:border-b-0 hover:bg-secondary/60 transition-colors"
                        data-testid={`news-item-${i}`}
                      >
                        <div className="text-sm font-medium leading-snug">{n.title}</div>
                        <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 font-mono">
                          {n.publisher ? <span>{n.publisher}</span> : null}
                          {n.link ? <ExternalLink className="h-3 w-3" strokeWidth={1.5} /> : null}
                        </div>
                        {n.summary ? <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{n.summary}</p> : null}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* VERDICT */}
          {tab === "verdict" && (
            <section data-testid="tab-content-verdict" className="space-y-5">
              {!a ? (
                <div className="card-noir p-8 text-sm text-muted-foreground rounded-lg">No analysis yet.</div>
              ) : (
                <>
                  <div className={`relative overflow-hidden border p-8 rounded-lg ${verdictStyle[a.verdict] || "card-noir"}`}>
                    <div className="text-[10px] uppercase tracking-[0.32em] font-mono font-bold opacity-80 mb-2">
                      Fund manager verdict
                    </div>
                    <div className="font-display text-6xl sm:text-7xl font-extrabold tracking-tighter">
                      {a.verdict?.replace("_", " ")}
                    </div>
                    <div className="text-xs uppercase tracking-[0.28em] mt-2 opacity-80 font-mono font-bold">
                      Conviction · {a.conviction}
                    </div>
                    <p className="text-sm mt-4 leading-relaxed max-w-3xl">{a.verdict_summary}</p>
                  </div>

                  <div className="card-noir p-5 rounded-lg">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-lime mb-3 font-semibold">Valuation take</div>
                    <p className="text-sm leading-relaxed">{a.valuation_take}</p>
                  </div>

                  {analysis?.generated_at ? (
                    <div className="text-[10px] text-muted-foreground font-mono uppercase tracking-[0.22em]">
                      Analysis generated {new Date(analysis.generated_at).toLocaleString()} · {analysis.model}
                    </div>
                  ) : null}
                </>
              )}
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
